import { Check, Circle, Lock, X, Minus, RotateCcw, ListChecks, Sparkles } from 'lucide-react';
import { useCopy, fill } from '../../i18n';
import { useOnboarding } from '../../contexts/OnboardingContext';

/**
 * Widget "Primeiros Passos": o eixo persistente do onboarding. Mostra o progresso real
 * (derivado do estado vivo) e traz o usuário de volta até concluir a ativação.
 * Flutua no canto inferior direito, minimizável e dispensável.
 */
export default function GettingStartedChecklist() {
  const t = useCopy().onboarding.checklist;
  const { sections, doneCount, totalCount, progress, allDone, dismissed, minimized, setMinimized, dismiss, restartTour } = useOnboarding();

  if (dismissed) return null;

  // Minimizado: só o launcher com o progresso.
  if (minimized) {
    return (
      <button
        type="button"
        data-testid="getting-started-launcher"
        onClick={() => setMinimized(false)}
        className="hidden md:flex fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-[var(--color-bg)] border border-[var(--color-border)] shadow-lg pl-3 pr-3.5 py-2.5 hover:border-[var(--color-primary)] hover:shadow-xl transition-all"
        aria-label={fill(t.ariaLauncher, { done: doneCount, total: totalCount })}
      >
        <span className="relative flex items-center justify-center w-6 h-6">
          <ListChecks size={18} className="text-[var(--color-primary)]" />
        </span>
        <span className="text-[13px] font-medium text-[var(--color-text)]">{t.title}</span>
        <span className="flex items-center justify-center min-w-[34px] h-[20px] px-1.5 rounded-full bg-[var(--color-primary)]/10 text-[11px] font-semibold text-[var(--color-primary)]">
          {doneCount}/{totalCount}
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid="getting-started-checklist"
      className="hidden md:block fixed bottom-4 right-4 z-40 w-[340px] max-w-[calc(100vw-2rem)] rounded-[var(--radius-lg)] bg-[var(--color-bg)] border border-[var(--color-border)] shadow-2xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
        <ListChecks size={18} className="text-[var(--color-primary)] shrink-0" />
        <span className="text-sm font-semibold text-[var(--color-text)] flex-1">{t.title}</span>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="p-1 rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
          aria-label={t.minimize}
        >
          <Minus size={16} />
        </button>
        <button
          type="button"
          data-testid="checklist-dismiss"
          onClick={dismiss}
          className="p-1 rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
          aria-label={t.close}
        >
          <X size={16} />
        </button>
      </div>

      {/* Progresso */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-[var(--color-text-secondary)]">
            {allDone ? t.allDone : fill(t.doneCount, { done: doneCount, total: totalCount })}
          </span>
          <span className="text-xs font-semibold text-[var(--color-primary)]">{progress}%</span>
        </div>
        <div
          data-testid="checklist-progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-1.5 w-full rounded-full bg-[var(--color-bg-tertiary)] overflow-hidden"
        >
          <div
            className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Itens, agrupados pelos grupos de processos do PMBoK */}
      <div className="px-2 py-1.5 max-h-[340px] overflow-y-auto">
        {sections.map((section) => (
          <section key={section.group} data-testid={`checklist-group-${section.group}`} className="mb-1.5 last:mb-0">
            <div className="flex items-center gap-2 px-2 pt-1.5 pb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                {t.groups[section.group]}
              </span>
              <span className="flex-1 h-px bg-[var(--color-border)]" />
              <span className="text-[11px] font-medium text-[var(--color-text-muted)] tabular-nums">
                {section.doneCount}/{section.totalCount}
              </span>
            </div>
            <ul>
              {section.items.map((item) => (
                <li
                  key={item.key}
                  data-testid={`checklist-item-${item.key}`}
                  data-state={item.done ? 'done' : 'pending'}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-[var(--radius-sm)]"
                >
                  {item.done ? (
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[var(--color-primary)] shrink-0">
                      <Check size={13} className="text-white" strokeWidth={3} />
                    </span>
                  ) : item.locked ? (
                    <span className="flex items-center justify-center w-5 h-5 shrink-0">
                      <Lock size={14} className="text-[var(--color-text-muted)]" />
                    </span>
                  ) : (
                    <span className="flex items-center justify-center w-5 h-5 shrink-0">
                      <Circle size={16} className="text-[var(--color-border)]" />
                    </span>
                  )}
                  <span className={`text-[13px] flex-1 ${item.done ? 'text-[var(--color-text-secondary)] line-through' : item.locked ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text)]'}`}>
                    {t.items[item.key]}
                  </span>
                  {item.locked && !item.done && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-info-bg)] text-[var(--color-info-text)] font-medium shrink-0">
                      Professional
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-[var(--color-border)]">
        <button
          type="button"
          data-testid="checklist-restart-tour"
          onClick={restartTour}
          className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors"
        >
          <RotateCcw size={13} />
          {t.restartTour}
        </button>
        {allDone && (
          <span className="flex items-center gap-1 text-[12px] font-medium text-[var(--color-primary)]">
            <Sparkles size={13} />
            {t.completed}
          </span>
        )}
      </div>
    </div>
  );
}
